import React from "react";
import { Button, Steps, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import urls from "urls";
import { PICK_DAY_STEPS } from "functions/pickDaySteps";

const { Text } = Typography;

/** The screen each step is taken on. */
const STEP_PAGE = { release: "orders", compose: "orders", print: "waves", pick: "waves", shortages: "shortages" };

const summaryOf = (t, step) => {
  switch (step.key) {
    case "release":
      return step.status === "finish" ? t("orderpicking.steps.release_done") : t("orderpicking.steps.release_count", { count: step.total });
    case "compose":
      if (step.unassigned > 0 && step.status !== "wait") return t("orderpicking.steps.compose_left", { count: step.unassigned });
      return step.rounds > 0 ? t("orderpicking.steps.compose_rounds", { count: step.rounds }) : null;
    case "print":
      return step.rounds > 0 ? t("orderpicking.steps.print_count", { printed: step.printed, rounds: step.rounds }) : null;
    case "pick":
      return step.rounds > 0 ? t("orderpicking.steps.pick_count", { picked: step.picked, rounds: step.rounds }) : null;
    case "shortages":
      if (step.open > 0) return t("orderpicking.steps.shortages_open", { count: step.open });
      return step.status === "finish" ? t("orderpicking.steps.shortages_none") : null;
    default:
      return null;
  }
};

/** How many the next step is about: orders without a round, rounds to print or pick, customers to call. */
const countFor = (step) =>
  ({
    release: step.total,
    compose: step.unassigned,
    print: step.rounds - step.printed,
    pick: step.rounds - step.picked,
    shortages: step.open,
  })[step.key];

/**
 * The stappenlijst of a pick day (functions/pickDaySteps): the five steps with how far each is,
 * and what to do next. On the screen of that step, `actions` can offer the step's own button
 * (Bestellingen: release, compose); elsewhere the button goes to the screen where it is done.
 */
export const PickDaySteps = ({ pickDay, deliveryDate, model, page, actions = {} }) => {
  const { t } = useTranslation();
  const links = {
    orders: `${urls.ordersList}?date=${deliveryDate || pickDay}`,
    waves: `${urls.pickWaves}?pickDay=${pickDay}`,
    shortages: `${urls.shortages}?pickDay=${pickDay}`,
  };

  let text = null;
  let action = null;
  if (model.empty) text = t("orderpicking.steps.next_empty");
  else if (model.finished) text = t("orderpicking.steps.next_finished");
  else if (model.current) {
    const step = model.steps.find((candidate) => candidate.key === model.current);
    const target = STEP_PAGE[model.current];
    text = t(`orderpicking.steps.next_${model.current}`, { count: countFor(step) });
    if (target === page && actions[model.current]) {
      action = (
        <Button type="primary" size="small" onClick={actions[model.current]}>
          {t(`orderpicking.steps.action_${model.current}`)}
        </Button>
      );
    } else if (target !== page) {
      action = (
        <Link to={links[target]}>
          <Button size="small" icon={<RightOutlined />}>
            {t(`orderpicking.steps.go_${target}`)}
          </Button>
        </Link>
      );
    }
  }

  const currentIndex = model.current ? PICK_DAY_STEPS.indexOf(model.current) : model.finished ? PICK_DAY_STEPS.length : -1;
  return (
    <div data-testid="pick-day-steps" style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: "12px 16px", marginBottom: 12, background: "#fff" }}>
      <Steps
        size="small"
        current={currentIndex}
        items={model.steps.map((step) => ({ title: t(`orderpicking.steps.${step.key}`), description: summaryOf(t, step), status: step.status }))}
      />
      {!!text && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Text strong>{t("orderpicking.steps.next")}</Text>
          <span>{text}</span>
          {action}
        </div>
      )}
    </div>
  );
};
